import { supabase } from './supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SenderContact {
    id: string;
    company_id: string;
    created_by: string;
    name: string;
    phone?: string;
    address?: string;
    city?: string;
    note?: string;
    tag?: string;
    is_default: boolean;
    created_at: string;
    updated_at: string;
}

export type ContactInput = Pick<SenderContact, 'name'> &
    Partial<Pick<SenderContact, 'phone' | 'address' | 'city' | 'note' | 'tag' | 'is_default'>>;

// ─── Service ──────────────────────────────────────────────────────────────────

export const ContactService = {
    /** 获取当前公司的所有联系人（名称升序） */
    async list(): Promise<SenderContact[]> {
        const { data, error } = await supabase
            .from('sender_contacts')
            .select('*')
            .order('is_default', { ascending: false })
            .order('name', { ascending: true });

        if (error) throw new Error(error.message);
        return data as SenderContact[];
    },

    /** 新增联系人 */
    async create(input: ContactInput, companyId: string): Promise<SenderContact> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('未登录');

        // 如果新增的是默认联系人，先清除其他默认
        if (input.is_default) {
            await supabase
                .from('sender_contacts')
                .update({ is_default: false })
                .eq('company_id', companyId);
        }

        const { data, error } = await supabase
            .from('sender_contacts')
            .insert({ ...input, company_id: companyId, created_by: user.id })
            .select('*')
            .single();

        if (error) throw new Error(error.message);
        return data as SenderContact;
    },

    /** 更新联系人 */
    async update(id: string, input: Partial<ContactInput>, companyId: string): Promise<SenderContact> {
        // 如果设为默认，先清除其他默认
        if (input.is_default) {
            await supabase
                .from('sender_contacts')
                .update({ is_default: false })
                .eq('company_id', companyId);
        }

        const { data, error } = await supabase
            .from('sender_contacts')
            .update(input)
            .eq('id', id)
            .select('*')
            .single();

        if (error) throw new Error(error.message);
        return data as SenderContact;
    },

    /** 删除联系人 */
    async remove(id: string): Promise<void> {
        const { error } = await supabase
            .from('sender_contacts')
            .delete()
            .eq('id', id);

        if (error) throw new Error(error.message);
    },

    /** 设置默认联系人 */
    async setDefault(id: string, companyId: string): Promise<void> {
        await supabase
            .from('sender_contacts')
            .update({ is_default: false })
            .eq('company_id', companyId);

        const { error } = await supabase
            .from('sender_contacts')
            .update({ is_default: true })
            .eq('id', id);

        if (error) throw new Error(error.message);
    },
};
